import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, NavLink, useParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import FileUploader from '../components/ui/FileUploader';
import QuizManager from '../components/admin/QuizManager';
import SectionManager from '../components/admin/SectionManager';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div style={styles.root}>
      <div style={styles.sidebar}>
        <div style={styles.sidebarBrand}>
          <div style={styles.logoMark}>SE</div>
          <div>
            <div style={styles.logoName}>Admin Panel</div>
            <div style={styles.logoSub}>Steady Education</div>
          </div>
        </div>
        <nav style={styles.nav}>
          {[
            { to: '/admin', label: 'Dashboard', icon: '📊', end: true },
            { to: '/admin/courses', label: 'Courses', icon: '📚' },
            { to: '/admin/packages', label: 'State Packages', icon: '📦' },
            { to: '/admin/users', label: 'Users', icon: '👥' },
            { to: '/admin/user-status', label: 'User Status', icon: '📈' },
            { to: '/admin/orders', label: 'Orders', icon: '💳' },
            { to: '/admin/referrals', label: 'Referrals', icon: '🎁' },
            { to: '/admin/attribution', label: 'Traffic Sources', icon: '🧭' },
            { to: '/admin/funnel', label: 'Funnel', icon: '🔻' },
            { to: '/admin/krec', label: 'KREC Reporting', icon: '🏛️' },
          ].map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              style={({ isActive }) => ({ ...styles.navItem, ...(isActive ? styles.navItemActive : {}) })}>
              <span>{item.icon}</span> {item.label}
            </NavLink>
          ))}
        </nav>
        <div style={styles.sidebarFooter}>
          <Link to="/" style={{ fontSize: 13, color: 'var(--slate-400)', textDecoration: 'none', display: 'block', marginBottom: 8 }}>← View Site</Link>
          <div style={styles.adminUser}>{user?.firstName} {user?.lastName}</div>
          <button style={styles.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>
      <div style={styles.content}>
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="courses" element={<AdminCourses />} />
          <Route path="courses/:courseId/builder" element={<CourseBuilder />} />
          <Route path="packages" element={<StatePackages />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="user-status" element={<UserStatus />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="referrals" element={<AdminReferrals />} />
          <Route path="attribution" element={<AdminAttribution />} />
          <Route path="funnel" element={<AdminFunnel />} />
          <Route path="krec" element={<AdminKrec />} />
        </Routes>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get('/admin/stats').then(r => setStats(r.data)); }, []);
  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Dashboard</h1>
      <div style={styles.statsGrid}>
        {[
          { label: 'Total Users', value: stats?.totalUsers ?? '—', icon: '👥', color: '#0d9488' },
          { label: 'Published Courses', value: stats?.totalCourses ?? '—', icon: '📚', color: '#7c3aed' },
          { label: 'Enrollments', value: stats?.totalEnrollments ?? '—', icon: '🎓', color: '#0891b2' },
          { label: 'Total Revenue', value: stats ? `$${parseFloat(stats.totalRevenue).toFixed(2)}` : '—', icon: '💰', color: '#16a34a' },
        ].map(s => (
          <div key={s.label} style={{ ...styles.statCard, borderTop: `3px solid ${s.color}` }}>
            <div style={styles.statIcon}>{s.icon}</div>
            <div style={{ ...styles.statValue, color: s.color }}>{s.value}</div>
            <div style={styles.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={styles.quickLinks}>
        <Link to="/admin/courses" className="btn btn-primary">Manage Courses →</Link>
        <Link to="/admin/packages" className="btn btn-secondary">State Packages →</Link>
        <Link to="/admin/users" className="btn btn-ghost">Manage Users →</Link>
        <Link to="/" className="btn btn-ghost" target="_blank">View Site ↗</Link>
      </div>
    </div>
  );
}

function StatePackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editPkg, setEditPkg] = useState(null);
  const [form, setForm] = useState({ stateCode:'AZ', stateName:'Arizona', packageName:'', packagePrice:49.99, packageDescription:'', regulatorName:'ADRE', requiredHours:24, isActive:true });
  const [message, setMessage] = useState('');

  const load = () => { setLoading(true); api.get('/admin/state-packages').then(r => setPackages(r.data.packages)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditPkg(null); setForm({ stateCode:'', stateName:'', packageName:'', packagePrice:49.99, packageDescription:'', regulatorName:'', requiredHours:24, isActive:true }); setShowForm(true); };
  const openEdit = (pkg) => { setEditPkg(pkg); setForm({ stateCode:pkg.state_code, stateName:pkg.state_name, packageName:pkg.package_name, packagePrice:pkg.package_price, packageDescription:pkg.package_description||'', regulatorName:pkg.regulator_name||'', requiredHours:pkg.required_hours, isActive:pkg.is_active }); setShowForm(true); };

  const handleStateSelect = (code) => {
    const state = US_STATES.find(s => s.code === code);
    setForm(f => ({ ...f, stateCode: code, stateName: state?.name || '' }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editPkg) await api.put(`/admin/state-packages/${form.stateCode}`, form);
      else await api.post('/admin/state-packages', form);
      setShowForm(false); setEditPkg(null);
      setMessage('State package saved!');
      setTimeout(() => setMessage(''), 3000);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to save'); }
  };

  const deletePkg = async (stateCode) => {
    if (!window.confirm(`Delete ${stateCode} package?`)) return;
    await api.delete(`/admin/state-packages/${stateCode}`);
    load();
  };

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>State Packages</h1>
          <p style={{ color:'var(--color-text-muted)', fontSize:14, marginTop:4 }}>Manage CE package pricing and settings for each state</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Add State</button>
      </div>
      {message && <div className="alert alert-success" style={{ marginBottom:20 }}>{message}</div>}
      {showForm && (
        <div style={styles.formCard}>
          <h3 style={{ marginBottom:20, fontSize:18, fontWeight:700 }}>{editPkg ? 'Edit State Package' : 'Add New State'}</h3>
          <form onSubmit={save} style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={styles.formRow}>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">State *</label>
                <select className="input" value={form.stateCode} onChange={e => handleStateSelect(e.target.value)} disabled={!!editPkg}>
                  <option value="">Select a state...</option>
                  {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Regulator Name *</label>
                <input className="input" placeholder="e.g. ADRE, DRE, TREC" value={form.regulatorName} onChange={e => setForm(f=>({...f,regulatorName:e.target.value}))} required />
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Required CE Hours *</label>
                <input className="input" type="number" value={form.requiredHours} onChange={e => setForm(f=>({...f,requiredHours:parseInt(e.target.value)}))} required />
              </div>
            </div>
            <div style={styles.formRow}>
              <div className="form-group" style={{ flex:2 }}>
                <label className="form-label">Package Name *</label>
                <input className="input" placeholder="e.g. Complete Arizona CE Package" value={form.packageName} onChange={e => setForm(f=>({...f,packageName:e.target.value}))} required />
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Package Price ($) *</label>
                <input className="input" type="number" step="0.01" value={form.packagePrice} onChange={e => setForm(f=>({...f,packagePrice:e.target.value}))} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Package Description</label>
              <textarea className="input" rows={2} value={form.packageDescription} onChange={e => setForm(f=>({...f,packageDescription:e.target.value}))} style={{ resize:'vertical' }} placeholder="Shown on the courses page below the package title" />
            </div>
            <div style={{ display:'flex', gap:20, alignItems:'center' }}>
              <label style={{ display:'flex', gap:8, alignItems:'center', fontSize:14, cursor:'pointer' }}>
                <input type="checkbox" checked={form.isActive} onChange={e => setForm(f=>({...f,isActive:e.target.checked}))} />
                Active (show package banner on courses page)
              </label>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-primary" type="submit">{editPkg ? 'Update Package' : 'Create Package'}</button>
              <button className="btn btn-ghost" type="button" onClick={() => { setShowForm(false); setEditPkg(null); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
        <div style={styles.table}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>State</th>
                <th style={styles.th}>Package Name</th>
                <th style={styles.th}>Regulator</th>
                <th style={styles.th}>CE Hours</th>
                <th style={styles.th}>Package Price</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {packages.length === 0 ? (
                <tr><td colSpan={7} style={{ ...styles.td, textAlign:'center', color:'var(--color-text-muted)', padding:40 }}>No state packages yet.</td></tr>
              ) : packages.map(pkg => (
                <tr key={pkg.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:36, height:36, background:'linear-gradient(135deg, #1d4142, #2a5c5e)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:13 }}>{pkg.state_code}</div>
                      <div style={{ fontWeight:600 }}>{pkg.state_name}</div>
                    </div>
                  </td>
                  <td style={styles.td}>{pkg.package_name}</td>
                  <td style={styles.td}><span className="badge badge-teal">{pkg.regulator_name}</span></td>
                  <td style={styles.td}>{pkg.required_hours} hrs</td>
                  <td style={styles.td}><span style={{ fontSize:18, fontWeight:800, color:'var(--forest-600)' }}>${parseFloat(pkg.package_price).toFixed(2)}</span></td>
                  <td style={styles.td}><span className={`badge ${pkg.is_active ? 'badge-success' : 'badge-slate'}`}>{pkg.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td style={styles.td}>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(pkg)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => deletePkg(pkg.state_code)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop:24, background:'var(--tan-50)', border:'1.5px solid #b39e89', borderRadius:12, padding:'16px 20px' }}>
        <p style={{ fontSize:14, color:'var(--forest-700)', fontWeight:600, marginBottom:4 }}>💡 How State Packages Work</p>
        <p style={{ fontSize:13, color:'var(--color-text-muted)', lineHeight:1.6 }}>Each state has its own package price and settings. When a user selects a state on the website, they see the matching package banner and price. Courses are filtered by state automatically.</p>
      </div>
    </div>
  );
}

const CREDIT_TYPES = ['core','elective','agency','commissioners_standard','contract_law','disclosure','fair_housing','real_estate_legal_issues','general'];

function AdminCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [stateFilter, setStateFilter] = useState('');
  const [form, setForm] = useState({ title:'', shortDescription:'', description:'', creditHours:3, creditType:'general', price:14.99, thumbnailUrl:'', isPublished:false, isFeatured:false, sortOrder:0, adreNumber:'', state:'AZ' });

  const load = () => { setLoading(true); api.get('/admin/courses').then(r => setCourses(r.data.courses)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditCourse(null); setForm({ title:'', shortDescription:'', description:'', creditHours:3, creditType:'general', price:14.99, thumbnailUrl:'', isPublished:false, isFeatured:false, sortOrder:courses.length, adreNumber:'', state:'AZ' }); setShowForm(true); };
  const openEdit = (c) => { setEditCourse(c); setForm({ title:c.title, shortDescription:c.short_description||'', description:c.description||'', creditHours:c.credit_hours, creditType:c.credit_type, price:c.price, thumbnailUrl:c.thumbnail_url||'', isPublished:c.is_published, isFeatured:c.is_featured, sortOrder:c.sort_order, adreNumber:c.adre_course_number||'', state:c.state||'AZ' }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editCourse) await api.put(`/admin/courses/${editCourse.id}`, form);
      else await api.post('/admin/courses', form);
      setShowForm(false); setEditCourse(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const togglePublish = async (c) => {
    await api.put(`/admin/courses/${c.id}`, { title:c.title, shortDescription:c.short_description, description:c.description, creditHours:c.credit_hours, creditType:c.credit_type, price:c.price, thumbnailUrl:c.thumbnail_url, isPublished:!c.is_published, isFeatured:c.is_featured, sortOrder:c.sort_order, adreNumber:c.adre_course_number, state:c.state });
    load();
  };

  const filtered = stateFilter ? courses.filter(c => c.state === stateFilter) : courses;
  const states = [...new Set(courses.map(c => c.state).filter(Boolean))].sort();

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Courses ({filtered.length})</h1>
        <div style={{ display:'flex', gap:10 }}>
          <select className="input" style={{ width:160 }} value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
            <option value="">All States</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="btn btn-primary" onClick={openNew}>+ New Course</button>
        </div>
      </div>
      {showForm && (
        <div style={styles.formCard}>
          <h3 style={{ marginBottom:20, fontSize:18, fontWeight:700 }}>{editCourse ? 'Edit Course' : 'New Course'}</h3>
          <form onSubmit={save} style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={styles.formRow}>
              <div className="form-group" style={{ flex:2 }}>
                <label className="form-label">Course Title *</label>
                <input className="input" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} required />
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">State *</label>
                <select className="input" value={form.state} onChange={e => setForm(f=>({...f,state:e.target.value}))}>
                  {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Credit Type *</label>
                <select className="input" value={form.creditType} onChange={e => setForm(f=>({...f,creditType:e.target.value}))}>
                  {CREDIT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g,' ').replace(/\b\w/g, m => m.toUpperCase())}</option>)}
                </select>
              </div>
            </div>
            <div style={styles.formRow}>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Price ($)</label>
                <input className="input" type="number" step="0.01" value={form.price} onChange={e => setForm(f=>({...f,price:e.target.value}))} />
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Credit Hours</label>
                <input className="input" type="number" step="0.5" value={form.creditHours} onChange={e => setForm(f=>({...f,creditHours:e.target.value}))} />
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Approval Course #</label>
                <input className="input" value={form.adreNumber} onChange={e => setForm(f=>({...f,adreNumber:e.target.value}))} placeholder="AZ-XXX-001" />
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Sort Order</label>
                <input className="input" type="number" value={form.sortOrder} onChange={e => setForm(f=>({...f,sortOrder:e.target.value}))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Short Description</label>
              <input className="input" value={form.shortDescription} onChange={e => setForm(f=>({...f,shortDescription:e.target.value}))} placeholder="One-line summary shown on course cards" />
            </div>
            <div className="form-group">
              <label className="form-label">Full Description</label>
              <textarea className="input" rows={4} value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} style={{ resize:'vertical' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Course Thumbnail</label>
              {form.thumbnailUrl && (
                <div style={{ marginBottom:10 }}>
                  <img src={form.thumbnailUrl} alt="thumbnail" style={{ width:120, height:80, objectFit:'cover', borderRadius:8, border:'1px solid var(--color-border)' }} />
                  <button type="button" style={{ display:'block', marginTop:4, fontSize:12, color:'var(--color-error)', background:'none', border:'none', cursor:'pointer' }} onClick={() => setForm(f=>({...f,thumbnailUrl:''}))}>Remove</button>
                </div>
              )}
              <FileUploader type="thumbnails" accept="image/*" label="Upload Thumbnail Image" hint="PNG, JPG up to 10MB" onUpload={url => setForm(f=>({...f,thumbnailUrl:url}))} />
              <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:13, color:'var(--color-text-muted)', whiteSpace:'nowrap' }}>Or paste URL:</span>
                <input className="input" placeholder="https://..." value={form.thumbnailUrl} onChange={e => setForm(f=>({...f,thumbnailUrl:e.target.value}))} />
              </div>
            </div>
            <div style={{ display:'flex', gap:20, alignItems:'center' }}>
              <label style={{ display:'flex', gap:8, alignItems:'center', fontSize:14, cursor:'pointer' }}>
                <input type="checkbox" checked={form.isPublished} onChange={e => setForm(f=>({...f,isPublished:e.target.checked}))} /> Published
              </label>
              <label style={{ display:'flex', gap:8, alignItems:'center', fontSize:14, cursor:'pointer' }}>
                <input type="checkbox" checked={form.isFeatured} onChange={e => setForm(f=>({...f,isFeatured:e.target.checked}))} /> Featured on Homepage
              </label>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-primary" type="submit">{editCourse ? 'Update Course' : 'Create Course'}</button>
              <button className="btn btn-ghost" type="button" onClick={() => { setShowForm(false); setEditCourse(null); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
        <div style={styles.table}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>Course</th>
                <th style={styles.th}>State</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Price</th>
                <th style={styles.th}>Enrolled</th>
                <th style={styles.th}>Slides</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      {c.thumbnail_url && <img src={c.thumbnail_url} alt="" style={{ width:40, height:28, objectFit:'cover', borderRadius:4 }} />}
                      <div>
                        <div style={{ fontWeight:600 }}>{c.title}</div>
                        <div style={{ fontSize:12, color:'var(--color-text-muted)' }}>{c.credit_hours} hrs • {c.adre_course_number}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ width:32, height:32, background:'linear-gradient(135deg, #1d4142, #2a5c5e)', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:11 }}>{c.state||'AZ'}</div>
                  </td>
                  <td style={styles.td}><span className="badge badge-teal" style={{ fontSize:11 }}>{c.credit_type?.replace(/_/g,' ')}</span></td>
                  <td style={styles.td}>${parseFloat(c.price).toFixed(2)}</td>
                  <td style={styles.td}>{c.enrollment_count||0}</td>
                  <td style={styles.td}>{c.slide_count||0}</td>
                  <td style={styles.td}><span className={`badge ${c.is_published ? 'badge-success' : 'badge-slate'}`}>{c.is_published ? 'Published' : 'Draft'}</span></td>
                  <td style={styles.td}>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/admin/courses/${c.id}/builder`)}>Builder</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}>Edit</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => togglePublish(c)}>{c.is_published ? 'Unpublish' : 'Publish'}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CourseBuilder() {
  const { courseId } = useParams();
  const [slides, setSlides] = useState([]);
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [sectionQuizzes, setSectionQuizzes] = useState([]);
  const [form, setForm] = useState({ title:'', content:'', slideType:'content', videoUrl:'', imageUrl:'', minTimeSeconds:120, sortOrder:0, sectionId:'' });
  const [bulkSectionId, setBulkSectionId] = useState('');
  const [bulkMsg, setBulkMsg] = useState('');

  const load = async () => {
    setLoading(true);
    const [slideRes, courseRes, sectionRes] = await Promise.all([
      api.get(`/admin/courses/${courseId}/slides`),
      api.get('/admin/courses'),
      api.get(`/admin/courses/${courseId}/sections`),
    ]);
    setSlides(slideRes.data.slides);
    setCourse(courseRes.data.courses.find(c => c.id === courseId));
    setSections(sectionRes.data.sections);
    setLoading(false);
  };

  useEffect(() => {
    load();
    api.get(`/quizzes/admin/course/${courseId}/section-quizzes`)
      .then(r => setSectionQuizzes(r.data.quizzes))
      .catch(() => {});
  }, [courseId]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) { await api.put(`/admin/courses/${courseId}/slides/${editId}`, form); setEditId(null); }
      else await api.post(`/admin/courses/${courseId}/slides`, form);
      setForm({ title:'', content:'', slideType:'content', videoUrl:'', imageUrl:'', minTimeSeconds:120, sortOrder:slides.length, sectionId:'' });
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const deleteSlide = async (slideId) => {
    if (!window.confirm('Delete this slide?')) return;
    await api.delete(`/admin/courses/${courseId}/slides/${slideId}`);
    load();
  };

  const startEdit = (slide) => {
    setEditId(slide.id);
    setForm({ title:slide.title||'', content:slide.content||'', slideType:slide.slide_type, videoUrl:slide.video_url||'', imageUrl:slide.image_url||'', minTimeSeconds:slide.min_time_seconds||0, sortOrder:slide.sort_order, sectionId:slide.section_id||'' });
    window.scrollTo({ top:0, behavior:'smooth' });
  };

  const handleBulkUpload = async (urls) => {
    const urlList = Array.isArray(urls) ? urls : [urls];
    for (let i = 0; i < urlList.length; i++) {
      const url = urlList[i];
      await api.post(`/admin/courses/${courseId}/slides`, {
        title: `Slide ${slides.length + i + 1}`,
        content: '',
        slideType: 'content',
        imageUrl: url,
        videoUrl: '',
        minTimeSeconds: 120,
        sortOrder: slides.length + i,
        sectionId: bulkSectionId || null,
      });
    }
    setBulkMsg(`Created ${urlList.length} slide(s)!`);
    setTimeout(() => setBulkMsg(''), 4000);
    load();
  };

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div>
          <Link to="/admin/courses" style={{ fontSize:13, color:'var(--color-primary)', textDecoration:'none' }}>← Back to Courses</Link>
          <h1 style={styles.pageTitle}>{course?.title || 'Course Builder'}</h1>
          <p style={{ color:'var(--color-text-muted)', fontSize:14 }}>{slides.length} slides • {course?.state || 'AZ'}</p>
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 400px', gap:24, alignItems:'start' }}>
        <div>
          <div style={{ ...styles.formCard, marginBottom:20 }}>
            <h3 style={{ marginBottom:4, fontSize:16, fontWeight:700 }}>📤 Bulk Upload Slides</h3>
            <p style={{ fontSize:13, color:'var(--color-text-muted)', marginBottom:16 }}>Upload multiple images or PDFs — each file becomes a new slide automatically.</p>
            <div className="form-group" style={{ marginBottom:16 }}>
              <label className="form-label">Assign to Section</label>
              <select className="input" value={bulkSectionId} onChange={e => setBulkSectionId(e.target.value)}>
                <option value="">— No section —</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
            </div>
            <FileUploader type="slides" multiple={true} accept="image/*,.pdf" label="Drop slide files here" hint="PNG, JPG, PDF — each file = one slide" onUpload={handleBulkUpload} />
            {bulkMsg && <div className="alert alert-success" style={{ marginTop:12 }}>✓ {bulkMsg}</div>}
          </div>
          {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
            <div style={styles.table}>
              {slides.length === 0 ? (
                <div style={{ padding:40, textAlign:'center', color:'var(--color-text-muted)' }}>No slides yet. Use the form or bulk upload →</div>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={styles.thead}>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>Slide</th>
                      <th style={styles.th}>Section</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Quiz</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slides.map((slide, i) => (
                      <tr key={slide.id} style={styles.tr}>
                        <td style={styles.td}>{i+1}</td>
                        <td style={styles.td}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            {slide.image_url && <img src={slide.image_url} alt="" style={{ width:36, height:28, objectFit:'cover', borderRadius:4 }} />}
                            <div style={{ fontWeight:600, fontSize:14 }}>{slide.title||'Untitled'}</div>
                          </div>
                        </td>
                        <td style={styles.td}>
                          {slide.section_title
                            ? <span className="badge badge-teal" style={{ fontSize:11 }}>{slide.section_title}</span>
                            : <span style={{ fontSize:12, color:'var(--color-text-muted)' }}>—</span>
                          }
                        </td>
                        <td style={styles.td}><span className={`badge ${slide.slide_type==='exam' ? 'badge-warning' : 'badge-slate'}`}>{slide.slide_type}</span></td>
                        <td style={styles.td}>
                          {slide.quiz_id
                            ? <span className="badge badge-success">✓ Quiz</span>
                            : <span style={{ fontSize:12, color:'var(--color-text-muted)' }}>—</span>
                          }
                        </td>
                        <td style={styles.td}>
                          <div style={{ display:'flex', gap:6 }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => startEdit(slide)}>Edit</button>
                            <button className="btn btn-danger btn-sm" onClick={() => deleteSlide(slide.id)}>Del</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
        <div style={styles.formCard}>
          <h3 style={{ marginBottom:16, fontSize:16, fontWeight:700 }}>{editId ? '✏️ Edit Slide' : '➕ Add Slide'}</h3>
          <form onSubmit={save} style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div className="form-group">
              <label className="form-label">Slide Title</label>
              <input className="input" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="input" value={form.slideType} onChange={e => setForm(f=>({...f,slideType:e.target.value}))}>
                <option value="content">Content</option>
                <option value="exam">Exam</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Slide Image</label>
              {form.imageUrl && (
                <div style={{ marginBottom:8 }}>
                  <img src={form.imageUrl} alt="slide" style={{ width:'100%', maxHeight:120, objectFit:'cover', borderRadius:8, border:'1px solid var(--color-border)' }} />
                  <button type="button" style={{ fontSize:12, color:'var(--color-error)', background:'none', border:'none', cursor:'pointer', marginTop:4 }} onClick={() => setForm(f=>({...f,imageUrl:''}))}>Remove image</button>
                </div>
              )}
              <FileUploader type="slides" accept="image/*" label="Upload slide image" hint="PNG, JPG up to 50MB" compact={true} onUpload={url => setForm(f=>({...f,imageUrl:url}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Video URL (YouTube/Vimeo embed)</label>
              <input className="input" placeholder="https://www.youtube.com/embed/..." value={form.videoUrl} onChange={e => setForm(f=>({...f,videoUrl:e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Content (HTML)</label>
              <textarea className="input" rows={6} value={form.content} onChange={e => setForm(f=>({...f,content:e.target.value}))} style={{ resize:'vertical', fontFamily:'monospace', fontSize:12 }} placeholder="<p>Your slide content here...</p>" />
            </div>
            <div style={styles.formRow}>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Section</label>
                <select className="input" value={form.sectionId||''} onChange={e => setForm(f=>({...f,sectionId:e.target.value||null}))}>
                  <option value="">— No section —</option>
                  {sections.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Sort Order</label>
                <input className="input" type="number" value={form.sortOrder} onChange={e => setForm(f=>({...f,sortOrder:parseInt(e.target.value)||0}))} />
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-primary" type="submit" style={{ flex:1 }}>{editId ? 'Update Slide' : 'Add Slide'}</button>
              {editId && <button className="btn btn-ghost" type="button" onClick={() => { setEditId(null); setForm({ title:'', content:'', slideType:'content', videoUrl:'', imageUrl:'', minTimeSeconds:120, sortOrder:slides.length, sectionId:'' }); }}>Cancel</button>}
            </div>
          </form>
        </div>
      </div>
      <div style={{ marginTop: 32 }}>
        <SectionManager courseId={courseId} />
      </div>
      <div style={{ marginTop: 32 }}>
        <QuizManager courseId={courseId} />
      </div>
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const load = (q) => { setLoading(true); api.get(`/admin/users${q ? `?search=${encodeURIComponent(q)}` : ''}`).then(r => setUsers(r.data.users)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const toggleStatus = async (u) => { await api.put(`/admin/users/${u.id}/status`, { isActive: !u.is_active }); load(search); };
  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Users</h1>
        <input className="input" style={{ width:280 }} placeholder="Search by name or email..." value={search} onChange={e => { setSearch(e.target.value); load(e.target.value); }} />
      </div>
      {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
        <div style={styles.table}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>User</th><th style={styles.th}>License #</th><th style={styles.th}>Role</th><th style={styles.th}>Status</th><th style={styles.th}>Joined</th><th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={styles.tr}>
                  <td style={styles.td}><div style={{ fontWeight:600 }}>{u.first_name} {u.last_name}</div><div style={{ fontSize:12, color:'var(--color-text-muted)' }}>{u.email}</div></td>
                  <td style={styles.td}>{u.license_number||'—'}</td>
                  <td style={styles.td}><span className={`badge ${u.role==='admin' ? 'badge-error' : 'badge-slate'}`}>{u.role}</span></td>
                  <td style={styles.td}><span className={`badge ${u.is_active ? 'badge-success' : 'badge-error'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td style={styles.td}>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td style={styles.td}><button className="btn btn-ghost btn-sm" onClick={() => toggleStatus(u)}>{u.is_active ? 'Deactivate' : 'Activate'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/admin/orders').then(r => setOrders(r.data.orders)).finally(() => setLoading(false)); }, []);
  const total = orders.reduce((s, o) => s + parseFloat(o.amount_total||0), 0);
  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Orders</h1>
        <div style={{ background:'var(--tan-50)', border:'1px solid var(--tan-200)', borderRadius:8, padding:'8px 16px', fontSize:14, fontWeight:700, color:'var(--forest-700)' }}>Total: ${total.toFixed(2)}</div>
      </div>
      {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
        <div style={styles.table}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={styles.thead}><th style={styles.th}>Customer</th><th style={styles.th}>Items</th><th style={styles.th}>Total</th><th style={styles.th}>Status</th><th style={styles.th}>Date</th></tr></thead>
            <tbody>
              {orders.length === 0 ? <tr><td colSpan={5} style={{ ...styles.td, textAlign:'center', color:'var(--color-text-muted)', padding:40 }}>No orders yet</td></tr>
              : orders.map(o => (
                <tr key={o.id} style={styles.tr}>
                  <td style={styles.td}><div style={{ fontWeight:600 }}>{o.first_name} {o.last_name}</div><div style={{ fontSize:12, color:'var(--color-text-muted)' }}>{o.email}</div></td>
                  <td style={styles.td}>{o.items?.map(i => i.title).join(', ')}</td>
                  <td style={styles.td}>${parseFloat(o.amount_total).toFixed(2)}</td>
                  <td style={styles.td}><span className={`badge ${o.status==='completed' ? 'badge-success' : 'badge-warning'}`}>{o.status}</span></td>
                  <td style={styles.td}>{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Summer Referral Program reconciliation. Attribution is derived from users.referred_by
// + orders on the backend; this view just surfaces who earned a reward and by when.
function AdminReferrals() {
  const [data, setData] = useState({ referrals: [], summary: {}, promo: {} });
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/admin/referrals').then(r => setData(r.data)).finally(() => setLoading(false)); }, []);
  const refs = data.referrals || [];
  const s = data.summary || {};
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const fmtMoney = (m) => m != null ? `$${parseFloat(m).toFixed(2)}` : '—';
  const badgeFor = (st) => st === 'qualified' ? 'badge-success' : st === 'pending' ? 'badge-warning' : 'badge-muted';

  const exportCsv = () => {
    const head = ['Referrer','Referrer Email','Referral Code','Referred Customer','Referred Email','Signup','Purchased','Order Amount','Order Date','Refund Closes','Send Reward By','Status'];
    const rows = [head].concat(refs.map(r => [
      `${r.referrer_first||''} ${r.referrer_last||''}`.trim(), r.referrer_email||'', r.referrer_code||'',
      `${r.referred_first||''} ${r.referred_last||''}`.trim(), r.referred_email||'',
      fmtDate(r.referred_signup), r.first_order_at ? 'Yes' : 'No', r.first_order_at ? fmtMoney(r.amount_total) : '',
      fmtDate(r.first_order_at), r.refund_closes ? fmtDate(r.refund_closes) : '', r.reward_send_by ? fmtDate(r.reward_send_by) : '', r.status,
    ]));
    const csv = rows.map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'steady-referrals.csv'; a.click();
  };

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Referrals</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Promo: {data.promo?.start} → {data.promo?.end}</span>
          <button className="btn btn-secondary btn-sm" onClick={exportCsv} disabled={!refs.length}>Export CSV</button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['Qualified (pay these)', s.qualified, 'var(--forest-700)'], ['Pending (no purchase yet)', s.pending, '#b45309'], ['Out of window', s.outOfWindow, 'var(--slate-500)']].map(([label, val, color]) => (
          <div key={label} style={{ background: 'var(--tan-50)', border: '1px solid var(--tan-200)', borderRadius: 8, padding: '10px 16px' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color }}>{val ?? 0}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{label}</div>
          </div>
        ))}
      </div>
      {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
        <div style={styles.table}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={styles.thead}>
              <th style={styles.th}>Referrer</th><th style={styles.th}>Referred customer</th><th style={styles.th}>Purchase</th>
              <th style={styles.th}>Refund closes</th><th style={styles.th}>Send reward by</th><th style={styles.th}>Status</th>
            </tr></thead>
            <tbody>
              {refs.length === 0 ? <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: 'var(--color-text-muted)', padding: 40 }}>No referrals yet</td></tr>
              : refs.map(r => (
                <tr key={r.referred_id} style={styles.tr}>
                  <td style={styles.td}><div style={{ fontWeight: 600 }}>{r.referrer_first} {r.referrer_last}</div><div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{r.referrer_code}</div></td>
                  <td style={styles.td}><div>{r.referred_first} {r.referred_last}</div><div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{r.referred_email}</div></td>
                  <td style={styles.td}>{r.first_order_at ? <><div>{fmtMoney(r.amount_total)}</div><div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{fmtDate(r.first_order_at)}</div></> : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}</td>
                  <td style={styles.td}>{fmtDate(r.refund_closes)}</td>
                  <td style={styles.td}>{fmtDate(r.reward_send_by)}</td>
                  <td style={styles.td}><span className={`badge ${badgeFor(r.status)}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminAttribution() {
  const [data, setData] = useState({ bySource: [], byLanding: [], coverage: {} });
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/admin/attribution').then(r => setData(r.data)).finally(() => setLoading(false)); }, []);

  const money = (m) => `$${parseFloat(m || 0).toFixed(2)}`;
  const pct = (a, b) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '—');
  const cov = data.coverage || {};
  const attributed = parseInt(cov.attributed || 0);
  const total = parseInt(cov.total || 0);

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Traffic Sources</h1>
        <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
          {attributed} of {total} accounts tracked
          {cov.tracking_since ? ` · since ${new Date(cov.tracking_since).toLocaleDateString()}` : ''}
        </span>
      </div>

      {attributed === 0 && !loading && (
        <div style={{ background: 'var(--tan-50)', border: '1.5px solid #b39e89', borderRadius: 10, padding: '14px 18px', marginBottom: 18, fontSize: 14 }}>
          No attributed signups yet. Accounts created before this shipped have no source,
          so this fills in as new people sign up.
        </div>
      )}

      {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
        <>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px', color: 'var(--forest-700)' }}>By source</h2>
          <div style={{ ...styles.table, marginBottom: 28 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={styles.thead}>
                <th style={styles.th}>Source</th><th style={styles.th}>Medium</th><th style={styles.th}>Signups</th>
                <th style={styles.th}>Customers</th><th style={styles.th}>Conversion</th><th style={styles.th}>Revenue</th>
              </tr></thead>
              <tbody>
                {data.bySource.length === 0 ? <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: 'var(--color-text-muted)', padding: 40 }}>Nothing yet</td></tr>
                : data.bySource.map(r => (
                  <tr key={`${r.source}-${r.medium}`} style={styles.tr}>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{r.source}</td>
                    <td style={styles.td}>{r.medium}</td>
                    <td style={styles.td}>{r.signups}</td>
                    <td style={styles.td}>{r.customers}</td>
                    <td style={styles.td}>{pct(parseInt(r.customers), parseInt(r.signups))}</td>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{money(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px', color: 'var(--forest-700)' }}>By landing page</h2>
          <div style={styles.table}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={styles.thead}>
                <th style={styles.th}>First page visited</th><th style={styles.th}>Signups</th>
                <th style={styles.th}>Customers</th><th style={styles.th}>Revenue</th>
              </tr></thead>
              <tbody>
                {data.byLanding.length === 0 ? <tr><td colSpan={4} style={{ ...styles.td, textAlign: 'center', color: 'var(--color-text-muted)', padding: 40 }}>Nothing yet</td></tr>
                : data.byLanding.map(r => (
                  <tr key={r.landing} style={styles.tr}>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 13 }}>{r.landing}</td>
                    <td style={styles.td}>{r.signups}</td>
                    <td style={styles.td}>{r.customers}</td>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{money(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// Map a 2-letter course state code to a display name (falls back to the code).
const STATE_LABELS = { AZ: 'Arizona', KS: 'Kansas', UT: 'Utah', NJ: 'New Jersey' };
const stateLabel = (code) => STATE_LABELS[code] || code;
const daysIdle = (dateStr) => {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
};

// Read-only student progress overview with a state filter. Click a student to see
// their full course breakdown (progress %, completion date, certificate).
function UserStatus() {
  const [data, setData] = useState({ users: [], availableStates: [] });
  const [stateFilter, setStateFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { user, courses }

  useEffect(() => {
    setLoading(true);
    const q = stateFilter !== 'ALL' ? `?state=${stateFilter}` : '';
    api.get(`/admin/user-status${q}`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [stateFilter]);

  const openUser = (u) => {
    setSelected({ user: u, courses: null });
    api.get(`/admin/users/${u.id}/progress`).then(r => setSelected(r.data)).catch(() => setSelected({ user: u, courses: [] }));
  };

  const q = search.trim().toLowerCase();
  const filtered = data.users.filter(u =>
    !q || `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
  );

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>User Status</h1>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          <select className="input" style={{ width:180 }} value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
            <option value="ALL">All States</option>
            {data.availableStates.map(code => <option key={code} value={code}>{stateLabel(code)}</option>)}
          </select>
          <input className="input" style={{ width:260 }} placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
        <>
          <p style={{ fontSize:13, color:'var(--color-text-muted)', marginBottom:12 }}>
            {filtered.length} student{filtered.length === 1 ? '' : 's'}{stateFilter !== 'ALL' ? ` in ${stateLabel(stateFilter)}` : ''} · sorted by most inactive first · click a row for course detail
          </p>
          <div style={styles.table}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={styles.thead}>
                <th style={styles.th}>Student</th>
                <th style={styles.th}>State</th>
                <th style={styles.th}>Progress</th>
                <th style={styles.th}>Avg %</th>
                <th style={styles.th}>Last Active</th>
                <th style={styles.th}>Idle</th>
              </tr></thead>
              <tbody>
                {filtered.map(u => {
                  const idle = daysIdle(u.last_active);
                  const done = u.completed === u.courses && u.courses > 0;
                  return (
                    <tr key={u.id} style={{ ...styles.tr, cursor:'pointer' }} onClick={() => openUser(u)}>
                      <td style={styles.td}>
                        <div style={{ fontWeight:600 }}>{u.first_name} {u.last_name}</div>
                        <div style={{ fontSize:12, color:'var(--color-text-muted)' }}>{u.email}</div>
                      </td>
                      <td style={styles.td}>{(u.states || []).map(stateLabel).join(', ')}</td>
                      <td style={styles.td}><span style={{ fontWeight:600, color: done ? 'var(--forest-700)' : 'var(--slate-700)' }}>{u.completed}/{u.courses} ✓</span></td>
                      <td style={styles.td}>{u.avg_pct}%</td>
                      <td style={styles.td}>{u.last_active ? new Date(u.last_active).toLocaleDateString() : '—'}</td>
                      <td style={styles.td}>{idle != null ? `${idle}d` : '—'}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td style={styles.td} colSpan={6}>No students found.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selected && (
        <div style={usStyles.overlay} onClick={() => setSelected(null)}>
          <div style={usStyles.modal} onClick={e => e.stopPropagation()}>
            <div style={usStyles.modalHeader}>
              <div>
                <div style={{ fontWeight:700, fontSize:18 }}>{selected.user.first_name} {selected.user.last_name}</div>
                <div style={{ fontSize:13, color:'var(--color-text-muted)' }}>{selected.user.email}</div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>✕ Close</button>
            </div>
            {!selected.courses ? <div className="loading-wrapper"><div className="spinner" /></div> : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr style={styles.thead}>
                  <th style={styles.th}>Course</th>
                  <th style={styles.th}>State</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Progress</th>
                  <th style={styles.th}>Started</th>
                  <th style={styles.th}>Completed</th>
                  <th style={styles.th}>Cert</th>
                </tr></thead>
                <tbody>
                  {selected.courses.map((c, i) => (
                    <tr key={i} style={styles.tr}>
                      <td style={styles.td}>{c.title}</td>
                      <td style={styles.td}>{stateLabel(c.state)}</td>
                      <td style={styles.td}><span className={`badge ${c.status === 'completed' ? 'badge-success' : 'badge-slate'}`}>{String(c.status).replace('_', ' ')}</span></td>
                      <td style={styles.td}>{c.progress_percent}%</td>
                      <td style={styles.td}>{c.verification_date ? new Date(c.verification_date).toLocaleDateString() : '—'}</td>
                      <td style={styles.td}>{c.completed_at ? new Date(c.completed_at).toLocaleDateString() : '—'}</td>
                      <td style={styles.td}>{c.certificate_issued ? '✅' : '—'}</td>
                    </tr>
                  ))}
                  {selected.courses.length === 0 && <tr><td style={styles.td} colSpan={7}>No enrollments.</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const usStyles = {
  overlay:{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'60px 20px', zIndex:1000, overflowY:'auto' },
  modal:{ background:'white', borderRadius:12, maxWidth:820, width:'100%', padding:24, boxShadow:'0 20px 60px rgba(0,0,0,0.25)' },
  modalHeader:{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, paddingBottom:16, borderBottom:'1px solid var(--color-border)' },
};

// KREC Reporting: Kansas completions waiting to be uploaded to KREC (5-calendar-day rule).
// The roster file is built server-side in KREC's exact format (utils/krec.js); the upload
// itself happens in the ACA portal, then completions are marked reported here.
function AdminKrec() {
  const [status, setStatus] = useState('unreported');
  const [data, setData] = useState({ completions: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [msg, setMsg] = useState('');

  const load = () => {
    setLoading(true);
    api.get(`/admin/krec/completions?status=${status}`)
      .then(r => { setData(r.data); setSelected([]); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  const rows = data.completions || [];
  const pending = rows.filter(r => !r.reportedAt);
  const toggle = id => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const allSelected = rows.length > 0 && rows.every(r => selected.includes(r.id));
  const toggleAll = () => setSelected(allSelected ? [] : rows.map(r => r.id));
  // "Mark as reported" only applies to selected lines that aren't reported yet.
  const selectedPending = pending.filter(r => selected.includes(r.id)).map(r => r.id);

  // Days until the deadline, in calendar days (deadline is MM/DD/YYYY).
  const daysLeft = (deadline) => {
    const [m, d, y] = deadline.split('/').map(Number);
    const due = Date.UTC(y, m - 1, d);
    const now = new Date();
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((due - today) / 86400000);
  };

  const download = async (ids) => {
    const res = await api.get(`/admin/roster/kansas${ids.length ? `?ids=${ids.join(',')}` : ''}`, { responseType: 'blob' });
    const name = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'krec-roster.txt';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(res.data);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const mark = async (ids, reported) => {
    if (!ids.length) return;
    if (reported && !window.confirm(`Mark ${ids.length} completion(s) as uploaded to KREC?`)) return;
    await api.post('/admin/krec/mark-reported', { ids, reported });
    setMsg(reported ? `Marked ${ids.length} as reported.` : `Moved ${ids.length} back to unreported.`);
    load();
  };

  const pill = (active) => ({ padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: '1.5px solid var(--forest-500)', background: active ? 'var(--forest-500)' : 'white', color: active ? 'white' : 'var(--forest-600)' });
  const btn = { padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'var(--forest-500)', color: 'white' };
  const btnLight = { ...btn, background: 'white', color: 'var(--forest-600)', border: '1.5px solid var(--forest-500)' };

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>KREC Reporting</h1>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={pill(status === 'unreported')} onClick={() => setStatus('unreported')}>To report</button>
          <button style={pill(status === 'all')} onClick={() => setStatus('all')}>All Kansas completions</button>
        </div>
      </div>

      <div style={{ background: 'var(--tan-50)', border: '1.5px solid #b39e89', borderRadius: 10, padding: '14px 18px', marginBottom: 18, fontSize: 14, lineHeight: 1.6 }}>
        KREC requires each completion within <strong>5 calendar days</strong> (or by the licensee's expiration date, if sooner).
        Download the roster, upload it at <a href="https://aca-prod.accela.com/KANSAS" target="_blank" rel="noopener noreferrer">aca-prod.accela.com/KANSAS</a>
        {' '}→ KREC Licenses → My Records → Education Provider Registration → Update Record → <strong>Course Roster Upload</strong>,
        then mark them reported here. help@ gets this file by email automatically while anything is waiting.
      </div>

      {msg && <div className="alert alert-success" style={{ marginBottom: 14 }}>{msg}</div>}

      {rows.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
          {selected.length > 0 && (
            <button style={btn} onClick={() => download(selected)}>Download selected ({selected.length}) .txt</button>
          )}
          <button style={selected.length ? btnLight : btn} onClick={() => download(rows.map(r => r.id))}>
            {status === 'unreported' ? `Download all to report (${rows.length}) .txt` : `Download all Kansas completions (${rows.length}) .txt`}
          </button>
          {pending.length > 0 && (
            <button style={btnLight} onClick={() => mark(selectedPending.length ? selectedPending : pending.map(r => r.id), true)}>
              Mark {selectedPending.length ? `${selectedPending.length} selected` : `all ${pending.length} unreported`} as reported
            </button>
          )}
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Tick the boxes to download or mark only some lines — they go into one KREC .txt file.</span>
        </div>
      )}

      {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
        <div style={styles.table}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={styles.thead}>
              <th style={styles.th}>{rows.length > 0 && <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all" />}</th><th style={styles.th}>Student</th><th style={styles.th}>Course</th>
              <th style={styles.th}>Completed</th><th style={styles.th}>Report by</th><th style={styles.th}>Roster line</th><th style={styles.th}>Status</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: 'var(--color-text-muted)', padding: 40 }}>
                  {status === 'unreported' ? 'Nothing waiting — every Kansas completion has been reported.' : 'No Kansas completions yet.'}
                </td></tr>
              ) : rows.map(r => {
                const left = daysLeft(r.deadline);
                const urgent = !r.reportedAt && left <= 2;
                return (
                  <tr key={r.id} style={{ ...styles.tr, ...(urgent ? { background: '#fef2f2' } : {}) }}>
                    <td style={styles.td}><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} aria-label={`Select ${r.student}`} /></td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{r.student}<div style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 400 }}>{r.email}</div></td>
                    <td style={styles.td}>{r.courseCode} · {r.course}</td>
                    <td style={styles.td}>{r.completedOn}</td>
                    <td style={{ ...styles.td, fontWeight: 700, color: urgent ? '#b91c1c' : undefined }}>
                      {r.deadline}{!r.reportedAt && <div style={{ fontSize: 12, fontWeight: 600 }}>{left < 0 ? `${-left} day(s) late` : left === 0 ? 'due today' : `${left} day(s) left`}</div>}
                    </td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>
                      {r.line}
                      {r.problems.length > 0 && <div style={{ color: '#b91c1c', fontFamily: 'inherit', fontWeight: 700 }}>Fix before upload: {r.problems.join(', ')}</div>}
                      <div>
                        <button style={{ background: 'none', border: 'none', color: 'var(--forest-600)', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '4px 0 0', fontFamily: 'inherit' }}
                          onClick={() => download([r.id])}>↓ Download .txt</button>
                      </div>
                    </td>
                    <td style={styles.td}>
                      {r.reportedAt ? (
                        <>
                          <span className="badge badge-success">Reported</span>
                          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{new Date(r.reportedAt).toLocaleDateString()}</div>
                          <button style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: 12, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                            onClick={() => mark([r.id], false)}>undo</button>
                        </>
                      ) : <span className="badge badge-warning">To report</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Funnel: where visitors drop off, step by step (routes/events.js → /admin/funnel).
function AdminFunnel() {
  const [days, setDays] = useState(7);
  const [segment, setSegment] = useState('new');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/funnel?days=${days}&segment=${segment}`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [days, segment]);

  const steps = data?.steps || [];
  const first = steps[0]?.total || 0;
  const pct = (a, b) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '—');
  const pill = (active) => ({ padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: '1.5px solid var(--forest-500)', background: active ? 'var(--forest-500)' : 'white', color: active ? 'white' : 'var(--forest-600)' });

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Funnel</h1>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[7, 30, 90].map(d => <button key={d} style={pill(days === d)} onClick={() => setDays(d)}>Last {d} days</button>)}
          <span style={{ width: 8 }} />
          <button style={pill(segment === 'new')} onClick={() => setSegment('new')}>New visitors</button>
          <button style={pill(segment === 'all')} onClick={() => setSegment('all')}>Everyone</button>
        </div>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '-6px 0 18px' }}>
        Each visitor is counted once per step they reached. "New visitors" leaves out returning students and staff.
        Tracking started October 2026, so earlier visits aren't included.
      </p>

      {loading || !data ? <div className="loading-wrapper"><div className="spinner" /></div> : (
        <>
          <div style={{ ...styles.table, marginBottom: 28 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={styles.thead}>
                <th style={styles.th}>Step</th><th style={styles.th}>Visitors</th><th style={styles.th}>Of previous step</th>
                <th style={styles.th}>Of all visitors</th><th style={styles.th}>Phone</th><th style={styles.th}>Computer</th>
              </tr></thead>
              <tbody>
                {first === 0 ? <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: 'var(--color-text-muted)', padding: 40 }}>No visits recorded yet in this period</td></tr>
                : steps.map((st, i) => (
                  <tr key={st.key} style={styles.tr}>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{i + 1}. {st.label}</td>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{st.total}</td>
                    <td style={styles.td}>{i === 0 ? '—' : pct(st.total, steps[i - 1].total)}</td>
                    <td style={styles.td}>{pct(st.total, first)}</td>
                    <td style={styles.td}>{st.mobile}</td>
                    <td style={styles.td}>{st.desktop}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {[['Where visitors arrived', data.landing], ["Last page seen (visitors who didn't buy)", data.exitPages]].map(([title, rows]) => (
              <div key={title}>
                <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px', color: 'var(--forest-700)' }}>{title}</h2>
                <div style={styles.table}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={styles.thead}><th style={styles.th}>Page</th><th style={styles.th}>Visitors</th></tr></thead>
                    <tbody>
                      {rows.length === 0 ? <tr><td colSpan={2} style={{ ...styles.td, textAlign: 'center', color: 'var(--color-text-muted)', padding: 24 }}>Nothing yet</td></tr>
                      : rows.map(r => (
                        <tr key={r.page} style={styles.tr}>
                          <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 13 }}>{r.page}</td>
                          <td style={styles.td}>{r.visitors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  root:{ height:'100vh', display:'flex', overflow:'hidden', background:'var(--slate-50)' },
  sidebar:{ width:240, background:'var(--slate-900)', display:'flex', flexDirection:'column', flexShrink:0 },
  sidebarBrand:{ padding:'20px 20px 16px', display:'flex', alignItems:'center', gap:12, borderBottom:'1px solid rgba(255,255,255,0.08)' },
  logoMark:{ width:36, height:36, background:'linear-gradient(135deg, #14b8a6, #0d9488)', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:13, flexShrink:0 },
  logoName:{ fontSize:14, fontWeight:700, color:'white' },
  logoSub:{ fontSize:11, color:'var(--slate-500)' },
  nav:{ flex:1, padding:'12px 10px', display:'flex', flexDirection:'column', gap:2 },
  navItem:{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px', borderRadius:8, fontSize:14, fontWeight:500, color:'var(--slate-400)', textDecoration:'none', transition:'all 0.15s' },
  navItemActive:{ background:'rgba(13,148,136,0.15)', color:'var(--teal-400)' },
  sidebarFooter:{ padding:'16px 20px', borderTop:'1px solid rgba(255,255,255,0.08)' },
  adminUser:{ fontSize:13, color:'var(--slate-400)', marginBottom:6 },
  logoutBtn:{ background:'none', border:'none', color:'var(--slate-500)', fontSize:13, cursor:'pointer', padding:0 },
  content:{ flex:1, overflow:'auto' },
  page:{ padding:'32px 40px', maxWidth:1400 },
  pageHeader:{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28, flexWrap:'wrap', gap:16 },
  pageTitle:{ fontFamily:'var(--font-display)', fontSize:26, color:'var(--slate-900)', marginBottom:0 },
  statsGrid:{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:20, marginBottom:32 },
  statCard:{ background:'white', border:'1px solid var(--color-border)', borderRadius:12, padding:'24px', textAlign:'center' },
  statIcon:{ fontSize:32, marginBottom:12 },
  statValue:{ fontSize:32, fontWeight:800, marginBottom:4 },
  statLabel:{ fontSize:14, color:'var(--color-text-muted)' },
  quickLinks:{ display:'flex', gap:12, flexWrap:'wrap' },
  table:{ background:'white', border:'1px solid var(--color-border)', borderRadius:12, overflow:'hidden' },
  thead:{ background:'var(--slate-50)' },
  th:{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'var(--slate-500)', textTransform:'uppercase', letterSpacing:'0.04em', borderBottom:'1px solid var(--color-border)', whiteSpace:'nowrap' },
  tr:{ borderBottom:'1px solid var(--color-border)' },
  td:{ padding:'14px 16px', fontSize:14, color:'var(--slate-700)', verticalAlign:'middle' },
  formCard:{ background:'white', border:'1px solid var(--color-border)', borderRadius:12, padding:'24px', marginBottom:20 },
  formRow:{ display:'flex', gap:12 },
};